import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import dimensionDefaultsService from "../services/dimensionDefaults";

const initialState = {
  dimensionDefaults: [],
  currentDimensionDefault: null,
  cubeDefaults: [],
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0
  },
  isLoading: false,
  error: null
};

// Get all dimension defaults with pagination
export const getDimensionDefaults = createAsyncThunk(
  "dimensionDefaults/getAll",
  async ({ page, pageSize }) => {
    const res = await dimensionDefaultsService.getDimensionDefaults(page, pageSize);
    return res;
  }
);

// Get dimension defaults for a specific cube
export const getDimensionDefaultsByCube = createAsyncThunk(
  "dimensionDefaults/getByCube",
  async (cubeId) => {
    const res = await dimensionDefaultsService.getDimensionDefaultsByCube(cubeId);
    return res;
  }
);

// Get dimension default by ID
export const getDimensionDefault = createAsyncThunk(
  "dimensionDefaults/getById",
  async (id) => {
    const res = await dimensionDefaultsService.getDimensionDefault(id);
    return res;
  }
);

// Get dimension default by cube and dimension
export const getDimensionDefaultByCubeAndDimension = createAsyncThunk(
  "dimensionDefaults/getByCubeAndDimension",
  async ({ cubeId, dimensionId }) => {
    const res = await dimensionDefaultsService.getDimensionDefaultByCubeAndDimension(
      cubeId,
      dimensionId
    );
    return res;
  }
);

// Create dimension default
export const createDimensionDefault = createAsyncThunk(
  "dimensionDefaults/create",
  async (data) => {
    const res = await dimensionDefaultsService.createDimensionDefault(data);
    return res;
  }
);

// Upsert dimension default
export const upsertDimensionDefault = createAsyncThunk(
  "dimensionDefaults/upsert",
  async (data) => {
    const res = await dimensionDefaultsService.upsertDimensionDefault(data);
    return res;
  }
);

// Bulk upsert dimension defaults
export const bulkUpsertDimensionDefaults = createAsyncThunk(
  "dimensionDefaults/bulkUpsert",
  async (data) => {
    const res = await dimensionDefaultsService.bulkUpsertDimensionDefaults(data);
    return res;
  }
);

// Update dimension default by ID
export const updateDimensionDefault = createAsyncThunk(
  "dimensionDefaults/update",
  async ({ id, data }) => {
    const res = await dimensionDefaultsService.updateDimensionDefault(id, data);
    return res;
  }
);

// Update dimension default by cube and dimension
export const updateDimensionDefaultByCubeAndDimension = createAsyncThunk(
  "dimensionDefaults/updateByCubeAndDimension",
  async ({ cubeId, dimensionId, data }) => {
    const res = await dimensionDefaultsService.updateDimensionDefaultByCubeAndDimension(
      cubeId,
      dimensionId,
      data
    );
    return res;
  }
);

// Delete dimension default by ID
export const deleteDimensionDefault = createAsyncThunk(
  "dimensionDefaults/delete",
  async (id) => {
    await dimensionDefaultsService.deleteDimensionDefault(id);
    return id;
  }
);

// Delete dimension default by cube and dimension
export const deleteDimensionDefaultByCubeAndDimension = createAsyncThunk(
  "dimensionDefaults/deleteByCubeAndDimension",
  async ({ cubeId, dimensionId }) => {
    await dimensionDefaultsService.deleteDimensionDefaultByCubeAndDimension(
      cubeId,
      dimensionId
    );
    return { cubeId, dimensionId };
  }
);

// Delete all dimension defaults for a cube
export const deleteDimensionDefaultsByCube = createAsyncThunk(
  "dimensionDefaults/deleteByCube",
  async (cubeId) => {
    await dimensionDefaultsService.deleteDimensionDefaultsByCube(cubeId);
    return cubeId;
  }
);

const dimensionDefaultsSlice = createSlice({
  name: "dimensionDefaults",
  initialState,
  reducers: {
    clearCurrentDimensionDefault: (state) => {
      state.currentDimensionDefault = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get all dimension defaults
      .addCase(getDimensionDefaults.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDimensionDefaults.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dimensionDefaults = action.payload.items || [];
        state.pagination = {
          page: action.payload.page,
          pageSize: action.payload.page_size,
          total: action.payload.total,
          totalPages: action.payload.total_pages
        };
      })
      .addCase(getDimensionDefaults.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      // Get dimension defaults by cube
      .addCase(getDimensionDefaultsByCube.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDimensionDefaultsByCube.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cubeDefaults = action.payload || [];
      })
      .addCase(getDimensionDefaultsByCube.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      // Get dimension default by ID
      .addCase(getDimensionDefault.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDimensionDefault.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentDimensionDefault = action.payload;
      })
      .addCase(getDimensionDefault.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      // Create dimension default
      .addCase(createDimensionDefault.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createDimensionDefault.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dimensionDefaults.push(action.payload);
      })
      .addCase(createDimensionDefault.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      // Upsert dimension default
      .addCase(upsertDimensionDefault.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(upsertDimensionDefault.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.dimensionDefaults.findIndex(
          dd => dd.id === action.payload.id
        );
        if (index !== -1) {
          state.dimensionDefaults[index] = action.payload;
        } else {
          state.dimensionDefaults.push(action.payload);
        }
      })
      .addCase(upsertDimensionDefault.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      // Bulk upsert
      .addCase(bulkUpsertDimensionDefaults.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkUpsertDimensionDefaults.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optionally update state with bulk results
      })
      .addCase(bulkUpsertDimensionDefaults.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      // Update dimension default
      .addCase(updateDimensionDefault.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDimensionDefault.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.dimensionDefaults.findIndex(
          dd => dd.id === action.payload.id
        );
        if (index !== -1) {
          state.dimensionDefaults[index] = action.payload;
        }
      })
      .addCase(updateDimensionDefault.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      // Delete dimension default
      .addCase(deleteDimensionDefault.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteDimensionDefault.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dimensionDefaults = state.dimensionDefaults.filter(
          dd => dd.id !== action.payload
        );
      })
      .addCase(deleteDimensionDefault.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });
  }
});

export const { clearCurrentDimensionDefault, clearError } = dimensionDefaultsSlice.actions;
export default dimensionDefaultsSlice.reducer;

